import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

const POLL_MS = 20000;

const authHeaders = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return { 'x-user-id': String(u?._id || u?.id || '') };
  } catch {
    return { 'x-user-id': '' };
  }
};

const Skeleton = () => (
  <div className="w-full animate-pulse rounded-2xl bg-white p-4" aria-busy="true" aria-label="Loading">
    <div className="h-8 w-2/3 rounded-xl bg-slate-200" />
    <div className="mt-4 aspect-video w-full rounded-xl bg-slate-200" />
    <div className="mt-4 h-16 w-full rounded-xl bg-slate-200" />
  </div>
);

/** Friendly calm illustration: a sleeping school bag. */
const SleepingBag = () => (
  <svg viewBox="0 0 200 160" className="mx-auto h-40 w-40" role="img" aria-label="A school bag sleeping">
    <ellipse cx="100" cy="146" rx="62" ry="8" fill="#cbd5e1" />
    <rect x="52" y="48" width="96" height="94" rx="26" fill="#60a5fa" stroke="#2563eb" strokeWidth="4" />
    <path d="M78 50c0-18 44-18 44 0" fill="none" stroke="#2563eb" strokeWidth="6" strokeLinecap="round" />
    <rect x="68" y="100" width="64" height="30" rx="12" fill="#93c5fd" stroke="#2563eb" strokeWidth="3" />
    <path d="M78 76q8 7 16 0M106 76q8 7 16 0" fill="none" stroke="#1e3a8a" strokeWidth="4" strokeLinecap="round" />
    <circle cx="100" cy="90" r="4" fill="#1e3a8a" />
    <text x="148" y="40" fontSize="20" fontWeight="bold" fill="#64748b">z</text>
    <text x="162" y="24" fontSize="26" fontWeight="bold" fill="#64748b">Z</text>
  </svg>
);

/**
 * Ultra-simple live classroom viewer for Student / Parent (also usable by Teacher).
 * Optional `classId` is only used by teachers/admins to preview a class.
 * Optional `onStatusChange(isLive)` lets a parent component sync its banner.
 */
export default function LiveClassroomViewer({ classId, onStatusChange }) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [muted, setMuted] = useState(true);
  const [frameKey, setFrameKey] = useState(0);

  const load = useCallback(async (signal, silent = false) => {
    if (!silent) setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const res = await axios.get(`${API_BASE_URL}/api/livestream/my-stream`, {
        headers: authHeaders(),
        params: classId ? { classId } : undefined,
        signal,
        timeout: 15000
      });
      if (!silent) setMuted(true);
      setState({ loading: false, error: '', data: res.data });
      if (onStatusChange) onStatusChange(!!res.data?.isLive);
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (silent) return; // keep showing what we already have
      const error =
        err.response?.status === 403
          ? 'You do not have access to this class.'
          : 'We could not load the class. Please check your internet and try again.';
      setState({ loading: false, error, data: null });
    }
  }, [classId, onStatusChange]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const timer = setInterval(() => load(controller.signal, true), POLL_MS);
    return () => { controller.abort(); clearInterval(timer); };
  }, [load]);

  // Unmute by reloading the embed with mute=0 (the tap is a user gesture,
  // so the browser/WebView permits audio playback).
  const embedUrl = state.data?.embedUrl || '';
  const embedSrc = muted ? embedUrl : embedUrl.replace('mute=1', 'mute=0');

  const handleUnmute = () => {
    setMuted(false);
    setFrameKey((k) => k + 1);
  };

  if (state.loading) return <Skeleton />;

  if (state.error) {
    return (
      <div role="alert" className="w-full rounded-2xl bg-white p-6 text-center shadow-md">
        <p className="text-xl font-bold text-slate-900">{state.error}</p>
        <button
          type="button"
          onClick={() => load()}
          className="mt-4 w-full rounded-xl bg-blue-600 py-4 text-xl font-bold text-white shadow-md active:scale-95"
        >
          🔄 Try Again
        </button>
      </div>
    );
  }

  // ---------- RECORDED: calm purple card, only when backend says hasRecording ----------
  if (!state.data?.isLive && state.data?.hasRecording === true && state.data?.embedUrl) {
    return (
      <div className="w-full overflow-hidden rounded-2xl bg-white shadow-md ring-2 ring-violet-300">
        <div className="bg-violet-50 px-4 py-4 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-violet-200 px-4 py-1.5 text-base font-bold text-violet-900">
            📼 PAST CLASS RECORDING
          </span>
          <h3 className="mt-3 text-2xl font-bold text-violet-950">Missed the class? Watch the Recording.</h3>
          <p className="mt-1 text-base text-violet-800">
            This is a recorded video of a previous session, not a live class.
          </p>
        </div>
        <div className="relative aspect-video w-full bg-black">
          <iframe
            src={state.data.embedUrl}
            title={`Recording: ${state.data.title || 'Past Class'}`}
            className="absolute inset-0 h-full w-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
        {state.data.title && (
          <p className="px-4 pt-3 text-center text-lg font-semibold text-slate-800">{state.data.title}</p>
        )}
        <div className="p-4">
          <button
            type="button"
            onClick={() => load()}
            className="w-full rounded-xl bg-violet-600 py-3 text-lg font-bold text-white shadow-md active:scale-95"
          >
            🔄 Refresh
          </button>
        </div>
      </div>
    );
  }

  // ---------- NOT LIVE: calm, soft gray ----------
  if (!state.data?.isLive) {
    return (
      <div className="w-full rounded-2xl bg-slate-100 p-6 text-center shadow-md ring-1 ring-slate-200 sm:p-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-slate-300 px-4 py-1.5 text-base font-bold text-slate-700">
          <span className="h-3 w-3 rounded-full bg-slate-500" aria-hidden="true" />
          NOT LIVE
        </span>
        <div className="mt-4">
          <SleepingBag />
        </div>
        <h3 className="mt-4 text-2xl font-bold text-slate-900">No Live Class Right Now.</h3>
        <p className="mx-auto mt-3 max-w-md text-lg text-slate-700">
          We will notify you here when the teacher starts the live session.
        </p>
        <button
          type="button"
          onClick={() => load()}
          className="mt-6 w-full rounded-xl bg-slate-700 py-4 text-xl font-bold text-white shadow-md active:scale-95"
        >
          🔄 Check Again
        </button>
      </div>
    );
  }

  // ---------- LIVE: vibrant red ----------
  return (
    <div className="w-full overflow-hidden rounded-2xl bg-white shadow-md ring-4 ring-rose-500">
      <div className="flex items-center gap-3 bg-rose-600 px-4 py-3 text-white">
        <span className="relative flex h-4 w-4 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
          <span className="relative inline-flex h-4 w-4 rounded-full bg-white" />
        </span>
        <span className="text-xl font-bold">🔴 LIVE NOW</span>
        {state.data.title && (
          <span className="ml-auto truncate text-lg font-bold">{state.data.title}</span>
        )}
      </div>

      <div className="relative aspect-video w-full bg-black">
        <iframe
          key={frameKey}
          src={embedSrc}
          title={state.data.title || 'Live Classroom'}
          className="absolute inset-0 h-full w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>

      <div className="p-4">
        {muted ? (
          <button
            type="button"
            onClick={handleUnmute}
            className="w-full rounded-xl bg-blue-600 py-4 text-xl font-bold text-white shadow-md transition hover:bg-blue-700 active:scale-95"
          >
            🔊 Tap Here to Listen (Unmute)
          </button>
        ) : (
          <p className="w-full rounded-xl bg-green-100 py-4 text-center text-xl font-bold text-green-900">
            ✅ Sound is ON
          </p>
        )}
      </div>
    </div>
  );
}

